/**
 * HelpGuidePage — lecture d'un guide (Batch #14.302) : sommaire latéral collant, sections ancrées, PDF aux couleurs du cabinet, sources.
 * Route : /admin/aide/:guideId
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, FileText } from "lucide-react";
import { api } from "@/api";
import { Btn, downloadPdf } from "./comptabilite/_shared";
import { ICONS, TONES } from "./aide/_help";
import { GuideBlock } from "./aide/GuideBlocks";

export default function HelpGuidePage() {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [guide, setGuide] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setGuide(null); api.get(`/help/guides/${guideId}`).then((r) => setGuide(r.data)).catch(() => setMissing(true)); }, [guideId]);
  useEffect(() => {
    if (!guide || !hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [guide, hash]);

  if (missing) return <div className="p-6 text-sm text-slate-600" data-testid="help-guide-missing">Guide introuvable. <button className="underline" onClick={() => navigate("/admin/aide")}>Retour à l'aide</button></div>;
  if (!guide) return <div className="p-6 text-sm text-slate-500">Chargement…</div>;
  const Icon = ICONS[guide.icon] || FileText;
  const tone = TONES[guide.tone] || TONES.slate;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid={`help-guide-${guide.id}`}>
      <div className={`bg-gradient-to-br ${tone.tile} border-2 rounded-2xl p-5 sm:p-6`}>
        <button onClick={() => navigate("/admin/aide")} className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3" data-testid="help-guide-back"><ArrowLeft className="w-3.5 h-3.5" /> Mode d'emploi et aide</button>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0"><Icon className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold">{guide.title}</h1>
            <p className="text-xs sm:text-sm opacity-80 mt-1 leading-snug">{guide.subtitle}</p>
            <p className="text-[10px] opacity-60 mt-2">{guide.category} · mis à jour le {guide.updated}</p>
          </div>
          <Btn tone="slate" icon={Download} busy={busy} onClick={() => downloadPdf(`/help/guides/${guide.id}.pdf`, `guide-${guide.id}.pdf`, setBusy)} testId="help-guide-pdf">Télécharger en PDF</Btn>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        <nav className="lg:sticky lg:top-4 self-start rounded-2xl border border-slate-200 bg-white p-3 text-sm" data-testid="help-guide-toc">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 px-2 mb-1">Sommaire</div>
          {guide.sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="block px-2 py-1.5 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 leading-snug" data-testid={`help-toc-${s.id}`}>{s.title}</a>
          ))}
        </nav>
        <article className="space-y-8">
          {guide.sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-4 space-y-4" data-testid={`help-section-${s.id}`}>
              <h2 className="text-base md:text-lg font-bold text-slate-900 border-b-2 border-slate-200 pb-1.5">{s.title}</h2>
              {s.blocks.map((b, i) => <GuideBlock key={i} block={b} sid={s.id} />)}
            </section>
          ))}
          {guide.sources?.length > 0 && (
            <section id="sources" className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="help-sources">
              <h2 className="text-sm font-bold text-slate-900">Sources et références</h2>
              <ul className="space-y-1">
                {guide.sources.map((src, i) => (
                  <li key={i}><a href={src.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline">{src.label} <ExternalLink className="w-3 h-3" /></a></li>
                ))}
              </ul>
            </section>
          )}
        </article>
      </div>
    </div>
  );
}
